/* eslint-disable no-plusplus */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/forbid-prop-types */
import React, { useRef, useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { isBrowser, isMobile } from 'react-device-detect';
import moment from 'moment';
import PropTypes from 'prop-types';
import { InputMask } from '@react-input/mask';
import CalendarLayout from './CalendarLayout';

const DaysOfWeek = () => {
  const weekdays = moment.weekdaysShort();
  return (
    <>
      <div
        className="d-flex flex-direction-row align-items-center justify-content-around w-100 mt-3"
        style={isMobile ? { maxHeight: '1rem' } : {}}>
        {weekdays.map((dayName) => (
          <p className="text-f6 upper-case" key={dayName}>
            <b>{dayName}</b>
          </p>
        ))}
      </div>
      <hr className={`bg-disabled opacity-100 ${isMobile && 'my-2 my-xl-0'}`} />
    </>
  );
};

const MonthView = ({ date, range, handleTileClick }) => {
  const today = useRef(moment());
  const startDay = date.clone().startOf('month').startOf('week');
  const endDay = date.clone().endOf('month').endOf('week');

  const _date = startDay.clone().subtract(1, 'day');

  const calendar = useMemo(() => {
    const _calendar = [];
    while (_date.isBefore(endDay, 'day')) {
      _calendar.push({
        days: Array(7)
          .fill(0)
          .map(() => _date.add(1, 'day').clone()),
      });
    }

    return _calendar;
  }, []);

  const month = date.month();

  return (
    <div className="calendar-month">
      <p className="text-center text-shadow text-f6 mt-3 mb-4">
        <b>{date.format('MMMM YYYY')}</b>
      </p>
      <div className="tiles-container">
        {calendar.map((week) =>
          week.days.map((day) => {
            const isFuture = day.isAfter(today.current);

            let inRange = false;
            let isStart = false;
            let isEnd = false;

            if (range.toDate) {
              inRange = day.isBetween(range.fromDate, range.toDate);

              if (range.toDate.format('L') === day.format('L')) {
                inRange = true;
                isEnd = true;
              }
            }

            if (range.fromDate) {
              if (range.fromDate.format('L') === day.format('L')) {
                inRange = true;
                isStart = true;
              }
            }

            const inMonth = month === day.month();
            return (
              <div
                tabIndex={-1}
                role="button"
                data-testid="calendar-tile-day"
                onKeyDown={() => {}}
                onClick={() => handleTileClick(day)}
                className={`tile ${isFuture && 'tile-disabled'} ${!inMonth && 'tile-hidden'} ${
                  inRange && 'tile-active'
                } ${isStart && 'tile-active-start'} ${isEnd && 'tile-active-end'} cursor-pointer`}
                key={day.date()}>
                <p>{day.date()}</p>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
MonthView.propTypes = {
  date: PropTypes.object.isRequired,
  handleTileClick: PropTypes.func.isRequired,
  range: PropTypes.object.isRequired,
};

const Calendar = ({ onSubmit, onHide, isVisible, clearOldData = () => {} }) => {
  const today = useRef(moment());
  const monthsToSub = 24;
  const { t: translate } = useTranslation();

  const QUICK_OPTIONS = [
    {
      label: `${translate('last_week_label')}`,
      fromDate: moment().startOf('day').subtract('7', 'days'),
    },
    {
      label: `${translate('last_month_label')}`,
      fromDate: moment().startOf('day').subtract('1', 'month'),
    },
    {
      label: `${translate('six_month_label')}`,
      fromDate: moment().startOf('day').subtract('6', 'months'),
    },
    {
      label: `${translate('year_label')}`,
      fromDate: moment().startOf('day').subtract('12', 'months'),
    },
  ];

  const [form, setForm] = useState({
    fromDate: null,
    toDate: null,
  });

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [formDateError, setFormDateError] = useState({
    fromDate: null,
    toDate: null,
  });

  const [selectedQuickOption, setSelectedQuickOption] = useState(null);

  const prevMonths = useMemo(() => {
    const result = [];
    for (let i = 0; i < monthsToSub; i++) {
      result.push(
        moment()
          .startOf('month')
          .subtract(monthsToSub - i, 'month')
      );
    }
    return result;
  }, [monthsToSub]);

  useEffect(() => {
    const bodyElement = document.querySelector('body');
    let backgroundStatus = {
      overflowY: 'unset',
      maxHeight: 'unset',
    };
    if (isVisible)
      backgroundStatus = {
        overflowY: 'hidden',
        maxHeight: '100vh',
      };
    Object.keys(backgroundStatus).forEach((k) => {
      bodyElement.style[k] = backgroundStatus[k];
    });
  }, [isVisible]);

  const onDateSelected = (date) => {
    if (form.fromDate && form.toDate) {
      setForm({ fromDate: null, toDate: null });
    } else if (date.isBefore(form.fromDate)) {
      setForm({ fromDate: null, toDate: null });
    } else if (form.fromDate) {
      setForm({ ...form, toDate: date });
      setToDate(date.format('L'));
      setFormDateError({ toDate: null });
    } else if (!form.fromDate) {
      setForm({ ...form, fromDate: date });
      setFromDate(date.format('L'));
      setFormDateError({ fromDate: null });
    }
    if (selectedQuickOption) {
      setSelectedQuickOption(null);
      setFromDate('');
      setToDate('');
    }
  };

  const handleCalendarReset = () => {
    setForm({ fromDate: null, toDate: null });
    setFromDate('');
    setToDate('');
    setSelectedQuickOption(null);
  };

  const handleInputValidation = (e) => {
    const { name, value } = e.target;
    const date = moment(moment(value), 'mm/dd/yyyy', true);

    if (!value) {
      setFormDateError({ ...formDateError, [name]: null });
      return;
    }

    if (!date.isValid()) {
      setFormDateError({
        ...formDateError,
        [name]: translate('calendar_invalid_date_label'),
      });
      return;
    }

    if (moment(toDate).isBefore(moment(fromDate))) {
      setFormDateError({
        ...formDateError,
        [name]: translate('calendar_incompatible_date_label')
          .replace('%s', translate('calendar_date_from_label'))
          .replace('%x', translate('calendar_date_to_label')),
      });
      return;
    }

    if (moment(toDate).isAfter(moment())) {
      setFormDateError({
        ...formDateError,
        toDate: translate('calendar_future_date_label'),
      });
      return;
    }

    setForm({ ...form, [name]: date });
    setFormDateError({ ...formDateError, [name]: null });
  };

  const onQuickOptionSelected = (label) => {
    setSelectedQuickOption(label);
  };

  const renderHeader = () => (
    <div className="d-flex flex-column">
      <div className="flex-row-center" style={isMobile ? { maxHeight: '1rem' } : {}}>
        <p className="d-flex flex-row align-items-center">
          <i className="icon-calendar-navigation text-dark text-f4" />
          <b className="ms-3">{translate('calendar_modal_title')}</b>
        </p>
        {!isMobile && (
          <div tabIndex={-1} role="button" onKeyDown={() => {}} onClick={handleCalendarReset}>
            <b className="text-primary text-f6 text-hover-dimmed">
              {translate('calendar_modal_reset_label')}
            </b>
          </div>
        )}
      </div>
      <DaysOfWeek />
    </div>
  );

  const renderHeaderMobile = () => (
    <div className="d-flex flex-column">
      <div className="d-flex justify-content-between align-items-center">
        <p className="d-flex flex-row align-items-center">
          <i className="icon-calendar-navigation text-dark text-f4" />
          <b className="ms-3">{translate('calendar_modal_title')}</b>
        </p>
        <div className="d-flex justify-content-end align-items-center">
          <i
            onClick={onHide}
            onKeyDown={onHide}
            role="button"
            className="ms-3 cursor-pointer icon icon-close-other text-dard text-f4"
            tabIndex={-1}
            aria-label="Close"
          />
        </div>
      </div>
      <DaysOfWeek />
    </div>
  );

  const renderFooter = () => (
    <>
      <div
        className={`flex-row-center mt-3 py-2 ${isMobile ? 'ps-0 pe-2 gap-1' : ''}`}
        style={isMobile ? { maxHeight: '3rem' } : {}}>
        {QUICK_OPTIONS.map((option) => (
          <button
            type="button"
            tabIndex={-1}
            onKeyDown={() => {}}
            onClick={() => {
              const from = option.fromDate;
              const to = moment().startOf('day');
              setFromDate(from.format('L'));
              setToDate(to.format('L'));
              setFormDateError({ fromDate: null, toDate: null });
              setForm({ fromDate: from, toDate: to });
              onQuickOptionSelected(option.label);
            }}
            key={option.label}
            style={{ flexShrink: 0 }}
            className={`calender-quick-option cursor-pointer rounded-4 py-1 px-3 text-white btn border-disabled text-f6 ${
              selectedQuickOption === option.label ? 'active' : ''
            }`}>
            <p>
              <b>{option.label}</b>
            </p>
          </button>
        ))}
      </div>
      <form
        className={`${isBrowser ? 'px-0' : 'ps-0 pe-2'}`}
        style={isMobile ? { maxHeight: '5rem' } : {}}>
        <div
          className={`d-flex gap-2 ${
            isMobile ? 'justify-content-between' : 'justify-content-center mt-4'
          }`}>
          <div className="form-group w-100 m-0 pe-3">
            <InputMask
              mask="mm/dd/yyyy"
              replacement={{ d: /\d/, m: /\d/, y: /\d/ }}
              className={`form-control-input calendar-input w-100 bg-transparent ${
                formDateError.fromDate ? 'is-invalid' : ''
              } ${isMobile ? 'text-f6' : ''}`}
              style={isMobile ? { maxHeight: '3rem' } : {}}
              placeholder={`${translate('calendar_date_from_label')} (MM/DD/YYYY)`}
              data-testid="calendar-input-control"
              onBlur={handleInputValidation}
              onChange={(e) => setFromDate(e.target.value)}
              name="fromDate"
              value={fromDate}
            />

            <label className="floating-label">{translate('calendar_date_from_label')}</label>
            <label className="invalid-feedback">
              {formDateError.fromDate || translate('calendar_invalid_date_label')}
            </label>
          </div>
          <div className="form-group w-100 m-0 ps-3">
            <InputMask
              mask="mm/dd/yyyy"
              replacement={{ d: /\d/, m: /\d/, y: /\d/ }}
              className={`form-control-input calendar-input w-100 bg-transparent ${
                formDateError.toDate ? 'is-invalid' : ''
              }
              ${isMobile ? 'text-f6' : ''}`}
              style={isMobile ? { maxHeight: '3rem' } : {}}
              placeholder={`${translate('calendar_date_to_label')} (MM/DD/YYYY)`}
              data-testid="calendar-input-control"
              onBlur={handleInputValidation}
              onChange={(e) => setToDate(e.target.value)}
              name="toDate"
              value={toDate}
            />

            <label className="floating-label">{translate('calendar_date_to_label')}</label>
            <label className="invalid-feedback">
              {formDateError.toDate || translate('calendar_invalid_date_label')}
            </label>
          </div>
        </div>
      </form>

      <div
        className={`w-100 mt-3 ${
          isMobile ? 'ps-0 pe-2 d-flex gap-2 justify-content-center' : 'p-0'
        }`}>
        {isMobile && (
          <button
            type="button"
            onKeyDown={() => {}}
            onClick={handleCalendarReset}
            className={`btn btn-outline-primary d-flex flex-grow-1 justify-content-center align-items-center ${
              isBrowser ? 'mx-1' : ''
            }`}
            style={isMobile ? { maxHeight: '3rem' } : {}}>
            {translate('calendar_modal_reset_label')}
          </button>
        )}

        <button
          type="button"
          data-testid="calendar-submit"
          onClick={() => {
            clearOldData();
            onSubmit(form);
            onHide();
          }}
          onKeyDown={() => {}}
          className={`btn btn-primary d-flex flex-grow-1 justify-content-center align-items-center ${
            !isMobile && 'w-100 mx-1'
          }`}
          style={isMobile ? { maxHeight: '3rem' } : {}}>
          {translate('calendar_date_apply_button')}
        </button>
      </div>
    </>
  );

  if (!isVisible) {
    return <></>;
  }

  return (
    <CalendarLayout
      onClose={onHide}
      renderHeaderMobile={renderHeaderMobile}
      renderHeader={renderHeader}
      renderFooter={renderFooter}>
      <div className={!isMobile ? 'calendar' : 'calendar-mobile'}>
        {prevMonths.map((prevMonth) => (
          <MonthView
            range={form}
            handleTileClick={onDateSelected}
            key={`${prevMonth.year()}/${prevMonth.month()}`}
            date={prevMonth.startOf('month')}
          />
        ))}
        <MonthView
          range={form}
          handleTileClick={onDateSelected}
          date={today.current.startOf('month')}
        />
      </div>
    </CalendarLayout>
  );
};

export default Calendar;

Calendar.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  isVisible: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  clearOldData: PropTypes.func,
};
